import { FC, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

import ChevronDownIcon from "../../../icons/ChevronDownIcon";
import CoreDatePicker from "../../../ui/core/CoreDatePicker";
import {
  CUSTOM_DATE_INTERVAL,
  DATE_INTERVAL_PRESET_OPTIONS,
  getDateIntervalLabel,
  getDateIntervalRangeLabel,
} from "../../../helpers/dateIntervalHelper";

interface ObservabilityDateIntervalSelectProps {
  dateInterval: string;
  customDateFrom: string;
  customDateTo: string;
  onPresetChange: (dateInterval: string) => void;
  onCustomRangeApply: (from: string, to: string) => void;
}

const TRIGGER_SX = {
  minWidth: 168,
  maxWidth: 280,
  height: 32,
  px: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.8125rem",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "4px",
  bgcolor: "background.paper",
  color: "text.primary",
  "&:hover": {
    bgcolor: "background.paper",
    borderColor: "primary.main",
  },
} as const;

const ObservabilityDateIntervalSelect: FC<
  ObservabilityDateIntervalSelectProps
> = ({
  dateInterval,
  customDateFrom,
  customDateTo,
  onPresetChange,
  onCustomRangeApply,
}) => {
  const openingCustomRef = useRef(false);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(customDateFrom);
  const [draftTo, setDraftTo] = useState(customDateTo);

  const today = dayjs().startOf("day");
  const minDate = draftFrom ? dayjs(draftFrom, "YYYY-MM-DD") : undefined;
  const endMaxDate = today;
  const draftToDate = draftTo ? dayjs(draftTo, "YYYY-MM-DD") : null;
  const startMaxDate =
    draftToDate && draftToDate.isBefore(today, "day")
      ? draftToDate
      : today;

  const presetOptions = useMemo(
    () =>
      DATE_INTERVAL_PRESET_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [],
  );

  const selectedRangeLabel = getDateIntervalRangeLabel(
    dateInterval,
    customDateFrom,
    customDateTo,
  );

  const triggerLabel = getDateIntervalLabel(dateInterval);
  const triggerText =
    selectedRangeLabel && dateInterval === CUSTOM_DATE_INTERVAL
      ? `${triggerLabel} · ${selectedRangeLabel}`
      : triggerLabel;

  const isMenuOpen = Boolean(anchorEl) && !isCustomOpen;

  const closeAll = () => {
    setAnchorEl(null);
    setIsCustomOpen(false);
  };

  const handleMenuClose = () => {
    if (openingCustomRef.current || isCustomOpen) {
      openingCustomRef.current = false;
      return;
    }

    setAnchorEl(null);
  };

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsCustomOpen(false);
  };

  const handlePresetSelect = (value: string) => {
    onPresetChange(value);
    closeAll();
  };

  const handleCustomMenuClick = () => {
    openingCustomRef.current = true;
    setDraftFrom(customDateFrom);
    setDraftTo(customDateTo);
    setIsCustomOpen(true);
  };

  const handleCustomCancel = () => {
    closeAll();
  };

  const handleCustomApply = () => {
    if (!draftFrom || !draftTo) {
      return;
    }

    onCustomRangeApply(draftFrom, draftTo);
    closeAll();
  };

  const canApplyCustom = Boolean(draftFrom && draftTo);

  return (
    <>
      <Button
        onClick={handleTriggerClick}
        sx={TRIGGER_SX}
        aria-haspopup="listbox"
        aria-expanded={isMenuOpen || isCustomOpen}
      >
        <Typography
          component="span"
          variant="body2"
          noWrap
          sx={{ fontSize: "inherit", fontWeight: "inherit", minWidth: 0 }}
        >
          {triggerText}
        </Typography>

        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          <ChevronDownIcon />
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { minWidth: 240, maxHeight: 360, mt: 0.5 },
          },
        }}
      >
        {presetOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={dateInterval === option.value}
            onClick={() => handlePresetSelect(option.value)}
            sx={{ py: 1 }}
          >
            <ListItemText
              primary={option.label}
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: dateInterval === option.value ? 600 : 400,
              }}
              secondaryTypographyProps={{
                fontSize: "0.75rem",
                color: "text.secondary",
              }}
            />
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          selected={dateInterval === CUSTOM_DATE_INTERVAL}
          onClick={handleCustomMenuClick}
          sx={{ py: 1 }}
        >
          <ListItemText
            primary="Custom range"
            primaryTypographyProps={{
              fontSize: "0.875rem",
              fontWeight: dateInterval === CUSTOM_DATE_INTERVAL ? 600 : 400,
            }}
            secondaryTypographyProps={{
              fontSize: "0.75rem",
              color: "text.secondary",
            }}
          />
        </MenuItem>
      </Menu>

      <Popover
        open={isCustomOpen}
        anchorEl={anchorEl}
        onClose={handleCustomCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { width: 280, p: 2, mt: 0.5 },
          },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Custom range
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <CoreDatePicker
            label="Start date"
            value={draftFrom}
            onChange={setDraftFrom}
            maxDate={startMaxDate}
          />
          <CoreDatePicker
            label="End date"
            value={draftTo}
            onChange={setDraftTo}
            minDate={minDate}
            maxDate={endMaxDate}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 2,
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={handleCustomCancel}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleCustomApply}
            disabled={!canApplyCustom}
            sx={{ textTransform: "none" }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default ObservabilityDateIntervalSelect;
